"use client";

import React, { DragEvent, ReactNode, useCallback, useRef } from "react";
import { useFileDropTarget } from "../hooks/useFileDropTarget";
import { memoGeneric } from "../utils/reactGenerics";

export type OffsetPoint = {
  offsetX: number;
  offsetY: number;
};

export type TypedFile<T> = File & { type: T };

export function isSupportedFile<T extends string>(
  file: File,
  supportedFileTypes: T[]
): file is TypedFile<T> {
  return (
    supportedFileTypes.includes(file.type as T) ||
    (file.type === "" && file.name.endsWith(".sketch"))
  );
}

type Props<T extends string> = {
  children: ReactNode | ((isActive: boolean) => ReactNode);
  onDropFiles: (file: TypedFile<T>[], offsetPoint: OffsetPoint) => void;
  supportedFileTypes: T[];
} & Omit<React.HTMLAttributes<HTMLDivElement>, "children">;

export const FileDropTarget = memoGeneric(function FileDropTarget<
  T extends string,
>({ children, onDropFiles, supportedFileTypes, ...props }: Props<T>) {
  const ref = useRef<HTMLDivElement>(null);

  const handleFile = useCallback(
    (event: DragEvent) => {
      event.preventDefault();

      const offsetPoint = {
        offsetX: event.nativeEvent.offsetX,
        offsetY: event.nativeEvent.offsetY,
      };

      const unsupportedTypes = Array.from(event.dataTransfer.files).flatMap(
        (file) => {
          if (!isSupportedFile(file, supportedFileTypes)) {
            return [file.type];
          } else {
            return [];
          }
        }
      );

      if (unsupportedTypes.length > 0) {
        alert(
          `Files of type ${unsupportedTypes.join(
            ", "
          )} aren't supported. The following types are supported: ${supportedFileTypes.join(
            ", "
          )}`
        );
      }

      const files = Array.from(event.dataTransfer.files).flatMap((file) =>
        isSupportedFile(file, supportedFileTypes) ? [file] : []
      );

      onDropFiles(files, offsetPoint);
    },
    [onDropFiles, supportedFileTypes]
  );

  const { dropTargetProps, isDropTargetActive } = useFileDropTarget(
    ref,
    handleFile
  );

  return (
    <div ref={ref} {...dropTargetProps} {...props}>
      {typeof children === "function" ? children(isDropTargetActive) : children}
    </div>
  );
});
